import * as Three from 'three';
import TWEEN from '@tweenjs/tween.js';

import {OPACITY_EXTRENAL_WALLS, OPACITY_INTERNAL_WALLS, KEY_KODES as keyCodes, RESOLUTION_SCALE_HIGH} from './modules/constants';
import EventEmitter from '../helpers/EventEmmiter';
import {getLayersObjects, prepareOptions} from './modules/helper';
import { OrbitControls } from './modules/libs/orbit-controls';
import {sortPoints, clockwiseDirections, normalizeYaw, normalizeCameraPosition} from './modules/libs/mathematics';
import {getVertexRenderCoords, getExtremeValue} from './modules/helper';
import {applyOpacity, applyDepthWrite, getMeshParentByProperty, createLabel} from './modules/three-helper';
import {disposeScene} from './modules/three-memory-cleaner';
import {ViewModel3D} from './modules/models';
import OrientationHelper from '../orientation.helper/orientation.helper';
import {E3CloseButton, E3Select} from '../ui';
import {addShaderPass, addEffectComposer, addRenderPass, addOutlinePass} from 'a_common-folder/webGL/postprocessing';
import {addCopyShader, addFXAAShader} from 'a_common-folder/webGL/shaders';
import TexturesService from "./modules/libs/textures.service";
const cloneDeep = require('lodash/cloneDeep');
import {RESOLUTION_SCALE_LOW} from "./modules/constants";
import {isMetrical} from "../service/geo.service";
import {getPolygonSquare, getPolygonBounding} from "a_common-folder/utils/geometry";
import {convertMetric} from "a_common-folder/utils/metric";
import {AREA, UNIT_METER} from "a_root-folder/constants";
import {LENGTH} from "a_common-folder/constants";
import {sortByAltitude} from "../helpers/common";
import DetectService from "../service/e3.viewer.detect.service";
import {getCustomAreaString} from "a_common-folder/utils/area";
import {gui} from "./modules/libs/gui";
import MeasurementTool from "./modules/libs/measurementTool"
import HelpService from '../service/help.service';

const throttle = require('lodash/throttle');
require('./e3plan-3d.scss');

const styles = {
    colors: {
        grey: '#1e2124'
    }
};
const topDownCoef = 0.001;
const detectBrowser = require('a_common-folder/utils/detectBrowser');

class E3Plan3D extends EventEmitter {
    _options = {
        width: 0,
        height: 0,
        previewMode: false,
        fitCanvasToModel: false,
        closeBtnClass: [],
        t_3d_model_dimensions: false,
        measurementMode: false
    };

    _lastMousePosition = {};
    _width = 0;
    _height = 0;
    _renderingID = 0;
    _wrapper = null;
    _renderer = null;
    _planConfig = {};
    _areasDataForTransparent = [];
    _wheelInterval = null;
    _updateBoundingBox = {
        min:{x:0,y:0,z:0},
        max:{x:0,y:0,z:0}
    };

    _orbitControls = null;
    _lines = null;
    _areas = null;
    _vertices = null;

    _camera = null;
    _scene3D = null;
    _planData = null;
    _spotLite = {
        src: null,
        target: null
    };

    // model functions bounds
    _updateVisibilityBound = null;
    _renderBound = null;

    // event bounds
    _mouseDownEventBound = null;
    _onTouchStartBound = null;
    _mouseUpEventBound = null;
    _touchMoveBound = null;
    _onWindowResizeBound = null;

    // animation movement
    _tween = null;
    _animationTimePointer = 0;
    _lastOffsetMouse = 0;
    _lastTouchClick = 0;

    _inited = false;
    _generated = 0;
    _orientationHelper = null;
    _floorSelect = null;
    _metricSelect = null;
    _closeButton = null;
    _isTouchDevice = false;

    _showAllFloors = {
        name: 'All',
        value: '0'
    };

    _view2D = false;
    _sceneAngle = 0;
    _current = {
        layerID: null,
    };
    _lastTeleportBounding = {x: 0, y: 0, z: 0};

    _initPosition = {
        '3d': {
            camera: new Three.Vector3(0,0,0),
            width: 0,
            height: 0,
            fov: 15
        },
        '2d': null
    };

    constructor(wrapper, plan, options = {}) {
        super();

        this._helperService = new HelpService();
        this._urlParams = this._helperService.urlParams;
        this._options = prepareOptions(this._options, options);
        this._detect = DetectService;
        let {width, height, previewMode} = this._options;

        this._lastMousePosition = {};
        this._width = width;
        this._height = height;
        // this._planConfig = this._applyRotateAngle(JSON.parse(JSON.stringify(plan)));
        this._planConfig = JSON.parse(JSON.stringify(plan));
        this._wrapper = wrapper;

        this._listener = this._progressListener.bind(this);
        this._isTouchDevice = 'ontouchstart' in document.documentElement;

        this._onWindowResizeBound = this._onWindowResize.bind(this, previewMode);
        window.addEventListener('resize', this._onWindowResizeBound, false);
        window.addEventListener('orientationchange', this._onWindowResizeBound, false);
    }

    _getLines() {
        let {layers} =  this._planConfig;

        return getLayersObjects('lines', layers);
    }

    _getAreaLines(areaID) {
        return this._planData.plan.children
            .filter((wall) => wall.areaID === areaID)
            .map((wall) => this._lines.find((line) => line.id === wall.identifier))
            .filter(Boolean);
    }

    _getAreas() {
        let {layers} =  this._planConfig;

        return getLayersObjects('areas', layers);
    }

    _getVertices() {
        let {layers} =  this._planConfig;

        return getLayersObjects('vertices', layers);
    }

    _getPoint(lines, prev, id = null) {
        const sceneUnit = this._planConfig.unit || 'mm';
        let obj = {};

        lines.map((line) => {
            if(line.id !== id){
                let start = getVertexRenderCoords(this._vertices.filter(ver => ver.id === line.vertices[0])[0], sceneUnit);
                let end =  getVertexRenderCoords(this._vertices.filter(ver => ver.id === line.vertices[1])[0], sceneUnit);

                end.y *= -1;
                start.y *= -1;

                if(start.x === prev.x && start.y === prev.y){
                    obj = {line: line, coord: [start, end]};
                }else if(end.x === prev.x && end.y === prev.y){
                    obj = {line: line, coord: [end, start]};
                }
            }
        });

        return obj;
    }

    _applyRotateAngle(plan, obj) {
        if (Object.keys(plan.layers).length > 1) return plan;

        let layerID = Object.keys(plan.layers).find((lid) => plan.layers[lid].altitude === 0);
        layerID = layerID || Object.keys(plan.layers)[0];

        const layer = plan.layers[layerID];
        const angles = {};
        let frequencyKey = 0;
        let frequencyVal = 0;

        Object.keys(layer.lines)
            .map((lineID) => {
                const line = layer.lines[lineID];

                const vector = line.vertices
                    .map((vid) => {
                        const {x, y} = layer.vertices[vid];

                        return [x, y]
                    })
                    .reduce((v1, v2) => [v2[0] - v1[0], v2[1] - v1[1]]);

                return Math.round(Math.atan2(vector[1], vector[0]) * 180 / Math.PI);
            })
            .forEach((angle) => {
                angles[angle] = !angles[angle] ? 1 : angles[angle] + 1;

                if (angles[angle] > frequencyVal) {
                    frequencyVal = angles[angle];
                    frequencyKey = angle;
                }
            });

        if (Math.abs(frequencyKey % 90) < 8) {
            this._sceneAngle = 0;

            return;
        }

        frequencyKey = Math.abs(frequencyKey);

        obj.rotateY(frequencyKey * Math.PI / 180);

        this._areasDataForTransparent = this._generateDataForTransparent(frequencyKey);
        this._sceneAngle = frequencyKey * Math.PI / 180;
    }

    /**
     * Rotate point around center point
     * @param x <number>
     * @param y <number>
     * @param center <number[]>
     * @param angle <number> - in deg (NOT rad)
     * @returns {{x: *, y: *}}
     * @private
     */
    _rotateVertex({x, y}, center, angle) {
        let dX = x - center[0];
        let dY = y - center[1];
        let r = Math.sqrt(dX * dX + dY * dY);
        let selfAngle = Math.atan2(dY, dX) * 180 / Math.PI;

        return {
            x: r * Math.cos((selfAngle + angle) * Math.PI / 180) + center[0],
            y: r * Math.sin((selfAngle + angle) * Math.PI / 180) + center[1]
        };
    }

    _startAutoRotate(){
        this._rotate(keyCodes.left, true, Math.PI/360/3.2, 7000/360);
    }

    _stopAutoRotate(){
        this._rotate(keyCodes.left, false);
    }

    startAutoRotate() {
        this._startAutoRotate();
    }

    stopAutoRotate() {
        this._stopAutoRotate();
    }

    isAutoRotateEnabled() {
        return !!this._wheelInterval;
    }

    _updateVisibilityForTransparent(){
        if(this.isNeedUpdateTexture() || !this._areasDataForTransparent.length || this._areasDataForTransparent.length !== this._planData.plan.children.filter((obj) => obj.name === 'area').length) {
            let sceneAngle = this._sceneAngle * 180 / Math.PI || 0;
            this._updateTexture = false;
            this.updateObjs();

            this._areasDataForTransparent = this._generateDataForTransparent(sceneAngle);
        }
        this._updateObjectVisibility();
    }

    _updateVisibility() {
        this._updateVisibilityForTransparentDebounce();
        this._render();
    }

    _generateDataForTransparent(sceneAngle) {
        const areasDataForTransparent = [];

        // rotate scene vertices
        if (sceneAngle) {
            // need to get layer with a first floor
            let lid = Object.keys(this._planConfig.layers).find((lid) => this._planConfig.layers[lid].altitude === 0);

            lid = lid || Object.keys(this._planConfig.layers)[0];

            const layerCenter = this._planConfig.layers[lid].center;

            this._vertices = this._vertices.map((vertex) => {
                let {x, y} = vertex;

                ({x, y} = this._rotateVertex({x, y}, layerCenter, sceneAngle));
                vertex.x = x;
                vertex.y = y;

                return vertex;
            })
        }

        this._areas.map((area) => {
            let lines = this._getAreaLines(area.id);
            if(!lines.length) return;
            let end = null, line = null, obj = null;
            let prev = sortPoints(this._generatePointArea(lines))[0];
            const clockWise = this._isClockWise(lines, prev);
            let objs = [];
            let v1 = null, v2 = null;

            for(let i = 0; i < lines.length; i++){
                if(i === 0){
                    prev.y *= -1;
                    obj = this._getPoint(lines, prev);
                } else {
                    obj = this._getPoint(lines, prev, line.id);
                    v2 = obj.coord;
                }
                v1 = obj.coord;
                end = obj.coord[1];
                line = obj.line;
                prev = end;

                objs.push(obj);
            }

            areasDataForTransparent.push({
                lines,
                clockWise,
                objs
            })
        });

        return areasDataForTransparent;
    }

    setBackground(color=styles.colors.grey) {
        const {previewMode} = this._options;
        let bgOpacity = previewMode ? 0 : 1;
        this._renderer.setClearColor(new Three.Color(color), bgOpacity);
    }

    _isClockWise(lines, prev) {
        let obj, end, line, points=[];
        let prevCoord = Object.assign({}, prev);

        for(let i = 0; i < lines.length; i++){
            if(i === 0){
                prevCoord.y *= -1;
                obj = this._getPoint(lines, prevCoord);
            }else{
                obj = this._getPoint(lines, prevCoord, line.id);
            }

            end = obj.coord[1];
            line = obj.line;
            prevCoord = end;
            points.push(obj.coord[0])
        }

        return clockwiseDirections(points);
    }

    _generatePointArea(lines) {
        const points = [];
        const sceneUnit = this._planConfig.unit || 'mm';

        lines.map((line) => {
            let start = getVertexRenderCoords(this._vertices.filter(ver => ver.id === line.vertices[0])[0], sceneUnit);
            let end =  getVertexRenderCoords(this._vertices.filter(ver => ver.id === line.vertices[1])[0], sceneUnit);

            points.push({x: start.x, y: start.y});
            points.push({x: end.x, y: end.y})
        });

        return points;
    }

    _updateObjectVisibility() {
        this._areasDataForTransparent.forEach((area) => {
            let line = null;
            const clockWise = area.clockWise;

            for(let i = 0; i < area.objs.length; i++){
                let obj =  area.objs[i];

                line = obj.line;
                let [start, end] = this._updateCoordWithNewCenter(...obj.coord);

                let x = end.x - start.x;
                let y = end.y - start.y;
                let z = end.z - start.z;

                // rotate 90 degrees CCW
                let normal = clockWise ? new Three.Vector3(y, z, -x) : new Three.Vector3(-y, z, x);

                normal.normalize();

                let position = this._orbitControls.object.position.clone();
                let focus = new Three.Vector3(
                    (start.x + end.x) / 2.0,
                    (start.z + end.z) / 2.0,
                    (start.y + end.y) / 2.0);
                let direction = position.sub(focus).normalize();

                // find dot
                let dot = normal.dot(direction);

                // update visible
                if(dot >= 0){
                    this._planData.plan.children.forEach((wall) => {
                        if(wall.identifier === line.id && !wall.hide && (!this._options.previewMode || !this._activeAreaID || wall.areaID === this._activeAreaID)) applyOpacity(wall, OPACITY_INTERNAL_WALLS);
                    })
                }else{
                    this._planData.plan.children.forEach((wall) => {
                        if(wall.identifier === line.id && !wall.hide && (!this._options.previewMode || !this._activeAreaID || wall.areaID === this._activeAreaID)) applyOpacity(wall, OPACITY_EXTRENAL_WALLS);
                    })
                }
            }
        })
    }

    _updateCoordWithNewCenter(start, end) {
        let newBoundingBox = this._updateBoundingBox;
        let newCenter = new Three.Vector3(
            (newBoundingBox.max.x - newBoundingBox.min.x) / 2 + newBoundingBox.min.x,
            (newBoundingBox.max.y - newBoundingBox.min.y) / 2 + newBoundingBox.min.y,
            (newBoundingBox.max.z - newBoundingBox.min.z) / 2 + newBoundingBox.min.z
        );

        let startVec = new Three.Vector3( start.x, 0, start.y).sub(newCenter);
        let endVec = new Three.Vector3( end.x, 0, end.y).sub(newCenter);

        return [{x:startVec.x, y: startVec.z,z: 0}, {x:endVec.x, y: endVec.z,z: 0}];
    }

    _touchMove(e) {
        e.preventDefault();
    }
    _sendMouseDownEvent(e) {
        this._lastMousePosition.x = e.offsetX / this._width * 2 - 1;
        this._lastMousePosition.y = -e.offsetY / this._height * 2 + 1;
    }

    _mouseDownEvent(e) {
        if (this._animationTimePointer) return;

        this._sendMouseDownEvent(e);
        this._stopAutoRotate();
    }

    _onTouchStart(e) {
        if (this._animationTimePointer) return;

        this._sendMouseDownEvent(e);
        this._stopAutoRotate();

        let offsetX = e.offsetX;
        let offsetY = e.offsetY;

        if(!offsetX || !offsetY){
            offsetX = e.touches[0].pageX;
            offsetY = e.touches[0].pageY;
        }
        this._lastOffsetMouse = {offsetX, offsetY};
        this._lastTouchClick = new Date().getTime();
    }

    _onTouchEnd(e) {
        if (this._animationTimePointer) return;

        this._stopAutoRotate();

        if(new Date().getTime() - this._lastTouchClick < 200){
            Object.assign(e, this._lastOffsetMouse);
            this._mouseUpEvent(e);
        }
    }

    _mouseUpEvent(e) {
        e.preventDefault();

        if (this._animationTimePointer) return;

        let mouse = new Three.Vector2();
        mouse.x = (e.offsetX / this._width) * 2 - 1;
        mouse.y = -(e.offsetY / this._height) * 2 + 1;

        if (
            Math.abs(mouse.x - this._lastMousePosition.x) > 0.02 ||
            Math.abs(mouse.y - this._lastMousePosition.y) > 0.02
        ) return;

        let raycaster = new Three.Raycaster();
        let toIntersect = [this._planData.plan],
            teleport, floor;

        raycaster.setFromCamera(mouse, this._camera);

        let intersects = raycaster.intersectObjects(toIntersect, true);

        if (intersects.length <= 0 && intersects[0] && isNaN(intersects[0].distance)) return;

        intersects.some((cross) => {
            if (cross.object && cross.object.name === 'floor' && !cross.object.parent.hide) floor = cross.object;

            teleport = getMeshParentByProperty(cross.object, 'name', 'teleport');

            if (teleport) teleport = this._isAreaObjectHidden(teleport.areaID) ? null : teleport;

            return floor || teleport;
        });

        if (!floor && !teleport) return;

        if (!teleport && floor) {
            let {areaID} = floor.parent;
            teleport = this._planData.plan.children.find((obj) => obj.name === 'teleport' && obj.areaID === areaID);
        }

        return teleport ? this._changeScene(teleport) : null;
    }

    changeScene(sceneID, sync=true) {
        const areaID = this._areas.find((area) => { return area.properties.scene.id===sceneID}).id;
        const teleport = this._planData.plan.children.find((obj) => obj.name === 'teleport' && obj.areaID === areaID);

        this._changeScene(teleport, sync);
    }

    _changeScene(teleport, sync=true) {
        let {areaID} = teleport;
        // todo: lock manipulation should trigger here;
        let promise = new Promise((resolve, reject) => {
            let sceneID;

            try {
                sceneID = this._areas
                    .find((area) => area.id === areaID)
                    .properties.scene.id
            } catch(e) {
                console.error(`Can't find area with ID = ${areaID}`);
                return;
            }

            this._fireEvent('scene.change.start', {sceneID, resolve, reject, sync})
        });

        promise
            .then((target) =>  this._moveIn(teleport, target))
            .catch((error) => {
                console.error(error);
            });
    }

    _onWindowResize(preview) {
        this._fireEvent('on.window.resize.start');
        let {offsetHeight: height, measurementMode, offsetWidth: width} = this._wrapper;

        if (!this._camera) return;

        if (preview) {
            const {width: maxW, height: maxH, aspect} = this._options;

            ({width, height} = this._getPreviewCanvasSizing(width, height, {maxW, maxH, aspect}));
        }

        this._width = width;
        this._height = height;

        this._camera.aspect = width / height;
        this._camera.updateProjectionMatrix();
        this._renderer.setSize(width, height);
        this._fireEvent('on.window.resize.end');
        this._render();
    }

    _getPreviewCanvasSizing(width, height, {maxW = 240, maxH = 240, aspect = 1}) {
        const minWidth = 150;
        const minHeight = 100;
        let widthWrap = window.innerWidth / 4;
        let heightWrap = window.innerHeight / 3;

        if (widthWrap < minWidth) widthWrap = minWidth;
        if (heightWrap < minHeight) heightWrap = minHeight;

        if (width > widthWrap) {
            width = widthWrap;
        } else {
            width = maxW > widthWrap ? widthWrap : maxW;
        }

        height = width / aspect;

        if (height > heightWrap) {
            height = maxH > heightWrap ? heightWrap : maxH;
            width = height * aspect;
        }

        return {width, height}
    }

    _updateLabelPosition() {
        this._planData.plan.children.filter((obj) => obj.name === 'teleport')
            .forEach((teleport) => {
                let object = teleport.children.find((_obj) => _obj.name === 'label');

                if (!object) return;

                let {max, min} = (new Three.Box3()).setFromObject(object);
                let {x, y, z} = this._camera.position;

                x += (max.x + min.x) / 2;
                z += (max.z + min.z) / 2;

                object.lookAt(x, y, z);
            })
    }

    _render() {
        const {previewMode, measurementMode} = this._options;

        this._camera.updateMatrix();
        this._camera.updateMatrixWorld();

        if (!previewMode) this._updateLabelPosition();

        for (let elemID in this._planData.sceneGraph.LODs) {
            this._planData.sceneGraph.LODs[elemID].update(this._camera);
        }
        this._renderer.setPixelRatio(window.devicePixelRatio);
        this._renderer.render(this._scene3D, this._camera);
        this._fireEvent('on.render')
    }

    _getMoveInTargetVectorPair = async (teleport, target, rotateDirection) => {
        let {yaw, pitch} = target;

        // yaw = normalizeYaw(yaw - rotateDirection + Math.PI / 2);
        yaw = normalizeYaw(yaw - rotateDirection - this._sceneAngle + Math.PI / 2);


        let tx = Math.cos(pitch)*Math.sin(yaw),
            tz = -Math.cos(pitch)*Math.cos(yaw),
            ty = Math.sin(pitch);

        const {x, y, z} = await this._getCameraPositionInTeleport(teleport);

        tx = tx * 100 + x;
        ty = ty * 100 + y;
        tz = tz * 100 + z;

        return {position: {x, y, z}, direction:{x: tx, y: ty, z: tz}}
    }

    /**
     *
     * @param st - startTarget
     * @param et - endTarget
     * @param sc - startCamera
     * @param ec - endCamera
     * @param cc - current camera
     * @returns {number[]}
     * @private
     */
    _getCurrentTarget(st, et, sc, ec, cc) {
        return Object.keys(cc).map((key) => {
            let value = ((cc[key] - sc[key]) * (et[key] - st[key])) / (ec[key] - sc[key]);

            value = isNaN(value) ? 0 : value;

            return value + st[key];
        });
    }

    _getTeleportsAll() {
        return this._planData.plan.children.filter((obj) => obj.name === 'teleport');
    }

    _moveIn = async (teleport, target) => {
        const {previewMode} = this._options;

        if (previewMode) return;

        let {promise} = target;

        const {areaID} = teleport;
        const {properties:{scene:{rotateDirection}}} = this._areas.find((area) => area.id === areaID);
        const {position,direction} = await this._getMoveInTargetVectorPair(teleport, target, rotateDirection);

        promise = promise || Promise.resolve();

        this._planData.plan.children
            .filter((obj) => obj.name === 'teleport')
            .forEach((_teleport) => this._toggleTeleportVisability(_teleport));

        return (this._view2D ? this._moveIn2D(position, direction) : this._moveIn3D(position, direction))
            .then(() => this._moveInComplete(promise));

        // this._animateCamera(position, direction)
        //     .then(() => this._moveInComplete(promise));
    }

    _moveIn2D(position, target) {
        // let {x, y, z} = this._camera.position;
        let currentPosition = Object.assign({}, this._camera.position);
        let {target: currentTarget} = this._orbitControls;

        let {maxKey} = getExtremeValue({x: Math.abs(currentPosition.x), z: Math.abs(currentPosition.z)});

        currentPosition[maxKey] = Math.abs(currentPosition[maxKey]) < 10
            ? currentPosition[maxKey] / (topDownCoef * 10)
            : currentPosition[maxKey] < 0
                ? currentPosition[maxKey] - 100
                : currentPosition[maxKey] + 100;

        return this._animateCamera(currentPosition, currentTarget, 500)
            .then(() => this._animateCamera(position, target, 500))
    }

    _moveIn3D(position, target) {
        return this._animateCamera(position, target);
    }

    _moveInComplete(promise) {
        return promise.then(() => {
            this._orbitControls.update();
            this._fireEvent('scene.change.end');
        })
    }

    _getStartCameraCoords(camera, boundingBox, offset = 1.25) {
        const x = -(boundingBox.max.x - boundingBox.min.x) / 2;
        const y = (boundingBox.max.y - boundingBox.min.y) * 4;
        const center = boundingBox.getCenter();
        const size = boundingBox.getSize();
        const maxDim = Math.max(size.x, size.y, size.z);
        const fov = camera.fov * (Math.PI / 180);
        const cameraZ = (maxDim / 2 / Math.tan(fov / 2)) * offset;
        const z = center.z + cameraZ;

        const minZ = boundingBox.min.z;
        const cameraMaxDistance = (minZ < 0) ? -minZ + cameraZ : cameraZ - minZ;

        return {vector:{x, y: y > 1000 ? 1000 : y, z}, maxDistance: cameraMaxDistance};
    }

    _getPreviewCameraSet(boundingBox, cameraPosition) {
        let fov, aspect, width, height, _width, _height,
            {x:_x, y:_y, z: _z} = cameraPosition;
        const offset = 20;
        const defaultWidth = this._width;
        const {x,y,z} = boundingBox.getSize();
        const angle = Math.atan(_y / (Math.sqrt(_x*_x + _z*_z)));

        _width = Math.sqrt(x * x + z * z);
        _width = (y > x) && (y > z) ? _width + (y * Math.cos(angle)) / 2 : _width;
        _height = _width * Math.sin(angle) + y * Math.cos(angle);

        const coef = defaultWidth / _width;

        height = coef * _height;
        width = defaultWidth;

        const delta = (Math.abs(defaultWidth - height) + offset) / (coef * Math.sin(angle));

        _x = _x - delta * 2;
        _z = _z + delta * 2;
        _y = Math.sqrt(_x * _x + _z * _z) * Math.tan(angle);

        const distance = Math.sqrt(_x * _x + _y * _y + _z * _z);
        const vFov = 2 * Math.atan(_height / (2 * distance));

        aspect = width / height;

        const hFov = 2 * Math.atan( Math.tan( vFov / 2 ) * aspect );

        fov = hFov > vFov ? hFov : vFov;
        fov *= (180 / Math.PI);

        return {fov, width, height, aspect, position: {x: _x, y: _y, z: _z}}
    }

    _moveOut() {
        const {previewMode} = this._options;
        const offset = window.innerHeight >= window.innerWidth ? 2 : 1;

        if (previewMode) return;

        this._setCameraPosition()
            .then(() => {
                this._planData.plan.children
                    .filter((obj) => obj.name === 'teleport')
                    .forEach((_teleport) => this._toggleTeleportVisability(_teleport, 1, 500, 500));

                return this._view2D ? this._animateMoveOut2D() : this._animateMoveOut3D(offset);
            })
            .then(() => this._orbitControls.enabled = true);
    }

    _animateMoveOut2D() {
        let {position} = this._get2dCameraPosition();

        position = Object.assign({}, position);

        const {x, y, z} = position;
        const target = {x:0, y:0, z:0};
        let {maxKey} = getExtremeValue({x: Math.abs(position.x), z: Math.abs(position.z)});

        position[maxKey] = Math.abs(position[maxKey]) < 10
            ? position[maxKey] / topDownCoef
            : position[maxKey] < 0
                ? position[maxKey] - 100
                : position[maxKey] + 100;

        target[maxKey] = this._camera.position[maxKey];

        return this._animateCamera(position, target, 500)
            .then(() => this._animateCamera({x, y, z}, {x: 0, y: 0, z: 0}, 500))
    }

    _animateMoveOut3D(offset) {
        const {x, y, z} = this._getStartCameraCoords(this._camera, this._planData.boundingBox, offset).vector;

        return this._animateCamera({x, y, z}, {x: 0, y: 0, z: 0})
    }

    _getCurrentSceneConfig() {
        return new Promise((resolve) => this._fireEvent('scene.get.current', {resolve}))
    }

    _isAreaObjectHidden(areaID) {
        const area = this._planData.plan.children.find((obj) => obj.name === 'area' && obj.areaID === areaID);

        return area ? area.hide : null;
    }

    _calculatePositionForScene({hfov, sceneID, yaw, pitch}) {
        const area = this._areas.find((area) => area.properties.scene.id === sceneID.toString());
        const {id, properties:{scene: {rotateDirection}}} = area;
        const teleport = this._planData.plan.children.find((obj) => obj.name === 'teleport' && obj.areaID === id);

        yaw = yaw * Math.PI / 180;
        pitch = pitch * Math.PI / 180;

        let vfov = this._hfovToVfov(hfov, this._width, this._height);

        if(this._width < this._height) {
            vfov = this._hfovToVfov(hfov, this._height, this._width);
        }
        this._camera.fov = vfov;
        this._camera.aspect = this._width / this._height;
        this._camera.updateProjectionMatrix();

        // window.camera = this._camera;
        // window.orbitControls = this._orbitControls;
        // window.render = this._render.bind(this);
        // window.Three = Three;

        return teleport
            ? this._getMoveInTargetVectorPair(teleport, {yaw, pitch}, rotateDirection)
            : {position: Object.assign({}, this._lastTeleportBounding), direction: {x: 0, y: 0, z: 0}};
    }

    _setCameraPosition() {
        return this._getCurrentSceneConfig()
            .then(this._calculatePositionForScene.bind(this))
            .then(async ({position, direction}) => {
                const {x: px, y: py, z: pz} = position;
                const {x: dx, y: dy, z: dz} = direction;

                this._camera.position.set(px, py, pz);
                this._orbitControls.target = new Three.Vector3(dx, dy, dz);
                this._orbitControls.update();

                this._render();
            })
    }

    _hfovToVfov(hfovDegrees, horizontalResolution, verticalResolution) {
        const hfovRadians = (hfovDegrees * Math.PI) / 180;
        const vfovRadians = 2 * Math.atan(Math.tan(hfovRadians / 2) * (verticalResolution / horizontalResolution));
        const vfovDegrees = (vfovRadians * 180) / Math.PI;

        return vfovDegrees;
    }

    setCameraPosition() {
        if(!this._camera) return ;

        this._camera.aspect = this._width / this._height;
        this._camera.updateProjectionMatrix();
        this._renderer.setSize(this._width, this._height);
        this._setCameraPosition();
    }

    _getTeleportBySceneID(sceneID) {
        const area = this._areas.find((area) => area.properties.scene.id === sceneID.toString());
        if(!area) return ;
        const {id} = area;

        return this._planData.plan.children.find((obj) => obj.name === 'teleport' && obj.areaID === id);
    }

    _getCameraPositionInTeleport(teleport) {
        const {max, min} = new Three.Box3().setFromObject(teleport);
        // const x = (max.x + min.x) / 2;
        // const x = max.x;
        // const y = (max.y + min.y) / 2;
        const y = max.y;
        // const z = (max.z + min.z) / 2;
        // const z = max.z;

        const box3 = new Three.Box3().setFromObject(teleport);
        const center = new Three.Vector3();
        const {x, z} = box3.getCenter(center);
        
        return {x, y, z};
    }

    _getTeleportBoundingBySceneID(sceneID) {
        const teleport = this._getTeleportBySceneID(sceneID);

        if (!teleport) {
            console.info(`Teleport is not available for sceneID = ${sceneID}`);

            // return {x: 0, y: 0, z: 0};
            return null;
        }

        return this._getCameraPositionInTeleport(teleport);
    }

    _highlightSceneTeleport(sceneID) {
        const teleport = this._getTeleportBySceneID(sceneID);
        if (this._options.previewMode) console.log('[highlight] sceneID:', sceneID, 'teleport found:', !!teleport, 'areas:', this._areas.map(a => a.properties.scene.id));
        if(!teleport) return ;

        this._current.layerID = this._getLayerIdBySceneId(sceneID);

        this._planData.plan.children.forEach((obj) => {
            if (obj.name === 'teleport') obj.userData.actions.deselect();
        });

        teleport.userData.actions.select();
        if (this._options.previewMode) {
            this._activeAreaID = teleport.areaID;
            this._highlightArea(teleport.areaID);
        }
    }

    _highlightArea(activeAreaID) {
        this._planData.plan.children.forEach((obj) => {
            if (obj.name !== 'area' && obj.name !== 'wall') return;
            const isActive = obj.areaID === activeAreaID;
            obj.traverse((child) => {
                if (!child.isMesh || !child.material) return;
                if (child.material.color) {
                    if (!child.material._origColor) {
                        child.material._origColor = child.material.color.clone();
                    }
                    if (child.material._origMap === undefined) {
                        child.material._origMap = child.material.map;
                    }
                    if (isActive) {
                        child.material.color.setHex(0xffffff);
                        child.material.map = null;
                    } else {
                        child.material.color.copy(child.material._origColor);
                        child.material.map = child.material._origMap;
                    }
                }
                child.material.transparent = true;
                child.material.opacity = isActive ? 1 : 0.3;
                child.material.alphaTest = isActive ? 0.5 : 0;
                child.material.needsUpdate = true;
            });
        });
        this._render();
    }

    _animateCamera({x, y, z}, {x: tx, y: ty, z: tz}, duration = 1000, onUpdate) {
        this._animationTimePointer = Date.now() + duration + 100;

        let {x: scx, y: scy, z: scz} = normalizeCameraPosition(this._camera.position);
        this._camera.position.set(scx, scy, scz);
        this._render();

        const startTarget = Object.assign({}, this._orbitControls.target);
        const endTarget = {x: tx, y: ty, z: tz};
        const startCamera = Object.assign({}, this._camera.position);
        const endCamera = {x, y, z};

        let promise = new Promise((resolve) => {
            this._tween.to({x, y, z}, duration)
                .onUpdate((vector) => {
                    const {x:_x, y:_y, z:_z} = vector;
                    const [ctx, cty, ctz] = this._getCurrentTarget(startTarget, endTarget, startCamera, endCamera, vector);

                    this._camera.position.set(_x, _y, _z);
                    this._orbitControls.target = new Three.Vector3(ctx, cty, ctz);

                    if (onUpdate) onUpdate({x,y,z});

                    this._orbitControls.update()
                })
                .onComplete(() => {
                    this._camera.position.set(x, y, z);
                    this._orbitControls.target = new Three.Vector3(tx, ty, tz);

                    if (onUpdate) onUpdate({x,y,z});

                    resolve();
                })
                .start();
        });

        this._animateLoop();

        // todo: need to be refactored (running out of sync query)
        setTimeout(() => {
            this._orbitControls.enabled = false;
        }, 50);

        return promise;
    }

    _toggleTeleportVisability(teleport, opacity = 0, duration = 500, delay = 0) {
        const {areaID} = teleport;

        if (this._isAreaObjectHidden(areaID)) return;

        teleport.children.forEach((childObj) => {
            if (childObj.name === 'circle') return;

            let tween = new TWEEN.Tween(childObj.material);

            tween.to({opacity}, duration)
                .onUpdate((obj) => childObj.material.opacity = obj.opacity)
                .delay(delay)
                .start()
        });
    }

    _animateLoop() {
        let timePointer = Date.now();

        if (this._animationTimePointer - timePointer > 0) {
            this._renderingID = requestAnimationFrame(() => this._animateLoop());
        } else {
            cancelAnimationFrame(this._renderingID);
            this._renderingID = null;
            this._animationTimePointer = null;
        }

        TWEEN.update();
    }

    addLightToScene(intensity = this._intensity){
        let light = new Three.AmbientLight(0xffffff, intensity);
        this._scene3D.add(light);
    }

    removeLightsFromScene(){
        this._scene3D.children.filter((obj) => obj instanceof  Three.AmbientLight)
            .forEach((obj) => this._scene3D.remove(obj))
        this._scene3D.children.filter((obj) => obj instanceof  Three.SpotLight)
            .forEach((obj) => this._scene3D.remove(obj))
    }

    _wheel(status, action, delay = 0.05, time = 50){
        if(status){
            this._wheelInterval = setInterval(() => {
                action(delay);
                this._orbitControls.update();
            }, time)
        } else {
            clearInterval(this._wheelInterval);
            this._wheelInterval = null;
        }
    }

    _rotate(vector, status, delay = 0.025, time = 50){
        switch (vector){
            case keyCodes.up:
                this._wheel(status, this._orbitControls.rotateUp, delay, time);
                break;
            case keyCodes.down:
                this._wheel(status, this._orbitControls.rotateUp, -delay, time);
                break;
            case keyCodes.right:
                this._wheel(status, this._orbitControls.rotateLeft, -delay, time);
                break;
            case keyCodes.left:
                this._wheel(status, this._orbitControls.rotateLeft, delay, time);
                break;
        }

        this._orbitControls.update();
    }

    _createLoading() {
        if (this._progress) {
            this._progress.remove();
            this._progress.style.opacity = '1';
            this._progressBar.style.width = '0%';
            this._progressElements = [];
        } else {
            this._progress = document.createElement('div');
            this._progress.className = 'progress';

            this._progressBar = document.createElement('div');
            this._progressBar.classList.add('progress-bar');
            this._progressBar.style.width = '0%';

            this._progress.appendChild(this._progressBar);
        }

        this._wrapper.appendChild(this._progress);
        this._wrapper.classList.add('loading-3d-plan');

        window.addEventListener('message', this._listener, false);
    }

    _removeLoading() {
        this._wrapper.classList.remove('loading-3d-plan');
        if (this._progress) this._progress.style.opacity = '0';

        window.removeEventListener('message', this._listener, false);
    }

    _triggerLoading(enable = null) {
        if (typeof enable === 'boolean') {
            return enable
                ? this._createLoading()
                : this._removeLoading();
        }
        if (this._wrapper.classList.contains('loading-3d-plan')) {
            this._removeLoading();
        } else {
            this._createLoading();
        }
    }

    _initOrientationHelper() {
        if (this._urlParams.intro_mouse_icon == 'false') return;
        if (this._orientationHelper) return;

        let opt = this._isTouchDevice ? {type: 'hand'} : {type: 'mouse'};
        this._orientationHelper = new OrientationHelper(this._wrapper, opt);

        this._orientationHelper.play();
        this._orientationHelperHide = this._orientationHelper.hide.bind(this._orientationHelper);
        this._wrapper.addEventListener('mousedown', this._orientationHelperHide, {capture: true, once: true});
        this._wrapper.addEventListener('touchstart', this._orientationHelperHide, {capture: true, once: true});
    }

    _destroyOrientationHelper() {
        this._wrapper.removeEventListener('mousedown', this._orientationHelperHide, {capture: true, once: true});
        this._wrapper.removeEventListener('touchstart', this._orientationHelperHide, {capture: true, once: true});
        this._orientationHelperHide();
        this._orientationHelper.destroy();

        this._orientationHelper = null;
    }

    _toggleFloorOpacity({floorID, showAll}) {
        this._planData.plan.children.forEach((obj) => {
            if (showAll) {
                applyOpacity(obj, OPACITY_INTERNAL_WALLS);
                obj.hide = false;
                this._changeDepthWrite(obj, true);
            } else {
                this._changeDepthWrite(obj, false);
                if (obj.layerID !== floorID) {
                    obj.hide = true;
                    applyOpacity(obj, OPACITY_EXTRENAL_WALLS);
                } else {
                    obj.hide = false;
                    applyOpacity(obj, OPACITY_INTERNAL_WALLS);
                }
            }
        })
    }

    _changeDepthWrite(obj, depthWrite){
        if(['ceiling', 'area'].includes(obj.name)){
            applyDepthWrite(obj, depthWrite)
        }
    }

    _toggleFloorVisability({floorID, showAll}) {
        const {measurementMode} = this._options;
        if(measurementMode)return ;
        
        this._planData.plan.children.forEach((obj) => {
            if (showAll) {
                // applyOpacity(obj, OPACITY_INTERNAL_WALLS);
                obj.visible = true;
            } else {
                if (obj.layerID !== floorID) {
                    obj.visible = false;
                    // applyOpacity(obj, OPACITY_EXTRENAL_WALLS);
                } else {
                    obj.visible = true;
                    // applyOpacity(obj, OPACITY_INTERNAL_WALLS);
                }
            }
        })
    }

    _initFloorSelect() {
        const {name, value} = this._showAllFloors;
        let floorList = Object.keys(this._planConfig.layers)
            .map((key) => this._planConfig.layers[key])
            .filter((floor) => !!Object.keys(floor.vertices).length);
        floorList = sortByAltitude(floorList);

        floorList = E3Select.convertToSelectArray(floorList,'name','id');

        let selectContainer = this._wrapper.getElementsByClassName('select-container')[0];

        if (!selectContainer) {
          selectContainer = document.createElement('div');
          selectContainer.classList.add('select-container');
          this._wrapper.appendChild(selectContainer);
        }

        const hidden = floorList.length <= 1;
        const floorSelect = new E3Select(
            selectContainer,
            {
                hidden,
                initValue: this._showAllFloors.value,
                className: ['e3plan-floor-select']
            },
            [
                {name, value},
                ...floorList
            ]
        );

        floorSelect.on('set.value', (e) => {
            const {value: floorID} = e;
            const showAll = floorID === this._showAllFloors.value;

            this._toggleFloorOpacity({floorID, showAll});
            this._updateVisibility();
        });

        return floorSelect;
    }

    _initMetricSelect() {
        const metricList = [
            {
                name: 'ft',
                value: '0'
            },
            {
                name: 'm',
                value: '1'
            }
        ];

        let selectContainer = this._wrapper.getElementsByClassName('select-container')[0];

        if (!selectContainer) {
            selectContainer = document.createElement('div');
            selectContainer.classList.add('select-container');
            this._wrapper.appendChild(selectContainer);
        }

        const metricSelect = new E3Select(
            selectContainer,
            {
                initValue: '1',
                className: ['e3plan-metric-select']
            },
            metricList
        );

        metricSelect.on('set.value', (e) => {
            const {value: metric} = e;

            this._updateMetricValues(metric);
        });

        return metricSelect;
    }

    initHiddenPreview() {
        this._stopAutoRotate();
        this._onWindowResize();
        setTimeout(() => {
            this.setCameraPosition();
        }, 0)
    }

    _updateMetricValues(metric) {
        this._metric = typeof metric === 'boolean' ? metric : !!Number(metric);

        this._updateTeleportLabels();

        if (this._floorSelect) {
            const floorID = this._floorSelect.get();

            this._floorSelect.set(floorID);
        }

        this._render();
    }

    _updateTeleportLabels() {
        this._planData.plan.children
            .filter(ch => ch.name === 'teleport')
            .forEach(teleport => {
                const area = this._areas.find(area => area.id === teleport.areaID);

                let polygon = area.vertices.map(vid => {
                    const vertex = this._vertices.find(v => v.id === vid);
                    const {x, y} = getVertexRenderCoords(vertex, this._planConfig.unit);

                    return [x, y];
                });

                const {minX, minY, maxX, maxY} = getPolygonBounding(polygon);
                const pWidth = maxX - minX;
                const pHeight = maxY - minY;

                let areaSize = getPolygonSquare(polygon);
                let {value: pv, unit: pu} = convertMetric(areaSize / 10000, AREA, this._metric, UNIT_METER, true, true, 1);
                let {value: wv, unit: wu} = convertMetric(pWidth / 100, LENGTH, this._metric, UNIT_METER, true, true, 1);
                let {value: hv, unit: hu} = convertMetric(pHeight / 100, LENGTH, this._metric, UNIT_METER, true, true, 1);

                const mergeArea = this.getMerge(area.id);

                if(mergeArea){
                    const {area: areaSize, width: pWidth, length: pHeight} = mergeArea;
                    
                    if(areaSize) {
                        let {value: pv1, unit: pu} = convertMetric(areaSize, AREA, this._metric, UNIT_METER, true, true, 1);
                        pv = pv1;
                    }
                    if(pWidth){
                        let {value: wv1, unit: wu} = convertMetric(pWidth, LENGTH, this._metric, UNIT_METER, true, true, 1);
                        wv = wv1;
                    }
                    if(pHeight){
                        let {value: hv1, unit: hu} = convertMetric(pHeight, LENGTH, this._metric, UNIT_METER, true, true, 1);
                        hv = hv1;
                    }
                }

                let areaString = this._metric
                    ? `${wv}x${hv}${hu}, ${pv}${pu}${String.fromCharCode(0xb2)}`
                    : `${wv}x${hv}, ${pv}${pu}${String.fromCharCode(0xb2)}`;

                const obj = area.properties;
                if(typeof obj.length === 'object' && typeof obj.width === 'object' && typeof obj.area === 'object'){
                    const widthLength = obj.width.length;
                    const squareLength = obj.area.length;
                    const lengthLength = obj.length.length;
                    const width = widthLength ? this.roundNumber(widthLength) : (isFinite(wv) ? wv : 0);
                    const square = squareLength ? this.roundNumber(squareLength) : (isFinite(pv) ? pv : 0);
                    const length = lengthLength ? this.roundNumber(lengthLength) : (isFinite(hv) ? hv : 0);

                    areaString = getCustomAreaString({width, unit: obj.length._unit, square, length}, this._metric, areaString);
                }

                let teleportLabel = teleport.children.find(obj => obj.name === 'label');

                if(teleportLabel){
                    const {title, size, color} = teleportLabel['userData'];

                    teleport.remove(teleportLabel);

                    if (title) {
                        let label = createLabel(title,  this._options.t_3d_model_dimensions ? areaString : '', 0, 0, 0, size, color);
                        label.name = 'label';
                        label.renderOrder = 1;

                        teleport.add(label);
                    }
                }
            });
    }

    getMerge(id) {
        const  {mergeAreas} = this._planConfig.layers[this._planConfig.selectedLayer];
        if(!mergeAreas) return null;
        let mergeArea = null;

        for(let i in mergeAreas){
            if(mergeAreas[i].connections.includes(id)) {
                mergeArea = mergeAreas[i];
                break;
            }
        }

        return mergeArea;
    }

    roundNumber (value, fraction = 2) {
        return parseFloat(value).toFixed(fraction);
    }

    _initCloseButton() {
        const {closeBtnClass} = this._options;
        const close = new E3CloseButton(this._wrapper, {
            classNames: ['absolute-top-right', ...closeBtnClass],
            events: {
                'click': this.hide.bind(this, true)
            }
        });

        close.show();

        return close;
    }

    _progressListener(obj) {
        if (!this._progressElements) {
            this._progressElements = [];
        }

        if (obj.data.event == 'loadPanoramaFailed') {
            if (this._error) {
                this._error.remove();
                this._wrapper.appendChild(this._error);
            } else {
                this._error = document.createElement('div');
                this._error.className = 'error';
                this._error.innerText = 'We have detected problems with your internet connection, please retry later.';
                this._wrapper.appendChild(this._error);
            }
        }

        if (obj.data.event == 'loadTexture') {
            if (!obj.data.url.includes('teleport.png') && !obj.data.url.includes('data:image')) {
                if (!this._texturesCount) {
                    this._texturesCount = 0;
                }

                this._texturesCount++;

                let percent = Math.round(this._texturesCount / (this._lines.length + this._areas.length * 2) * 100);

                this._progressBar.style.width = percent.toString() + '%';

                if (percent == 100) {
                    this._texturesCount = 0
                }
            }
        }

        if (obj.data.event == 'loadPanorama') {
            this._progressElements[obj.data.url] = obj.data.loaded / obj.data.total;

            let allProgress = 0;

            Object.keys(this._progressElements).forEach((k) => {
                allProgress += this._progressElements[k];
            });

            if(this._areas) {
                let percent = Math.round(allProgress / this._areas.length * 100);

                this._progressBar.style.width = percent.toString() + '%';

                if (percent == 100) {
                    this._progressElements = [];
                }
            }
        }

        if (obj.data.event == 'parseTexture') {
            let progress = 0;
            if(obj.data.url || obj.data.urlBase64) {
                progress = obj.data.loaded / obj.data.total / 2;
            }else{
                progress = 0.5 + (obj.data.loaded / obj.data.total / 2);
            }

            const percent = Math.round(progress * 100);
            
            this._progressBar.style.width = percent.toString() + '%';
        }
    }

    _clearDataFromTransparent() {
        this._areasDataForTransparent = [];
    }

    _setInitCameraPosition(key, position) {
        if (this._initPosition[key]) return;

        this._initPosition[key] = position;
    }

    _calculateCameraHeight(position) {
        const size = this._planData.boundingBox.getSize();
        const {x:sx,y:sy,z:sz} = size;
        const {maxKey, minKey} = getExtremeValue({x:sx,z:sz});
        const fov = this._camera.fov * Math.PI / 180;
        const aspect = this._width / this._height;

        // maxKey will be always horizontal (means width),
        // minKey will be always vertical (means height)

        const res1 = (size[minKey] + sy * Math.tan(fov/2)) / (2 * Math.tan(fov/2));
        const res2 = (size[maxKey] + sy * Math.tan(fov/2)) / (2 * Math.tan(fov/2) * aspect);

        position.y = res1 > res2 ? res1 : res2;

        return {position, maxKey, minKey};
    }

    _getActiveTeleport() {
        return this._planData && this._planData.plan ? this._planData.plan.children.find((obj) => obj.name === 'teleport' && obj.active) : null
    }

    updateFloorVisibility(sceneId) {
        const floorID = this._getLayerIdBySceneId(sceneId);

        if (floorID) {
            // this._toggleFloorOpacity({floorID});
            this._toggleFloorVisability({floorID});
            this._render();
        }
    }

    updateFloorOpacity(sceneId) {
        const floorID = this._getLayerIdBySceneId(sceneId);

        if (!floorID) return;

        this._toggleFloorOpacity({floorID});
        this._render();
    }

    parseTextures(ignoreParse = false) {
        return TexturesService.parseTextures(ignoreParse, this._planConfig);
    }

    toggleView(view2D = false, autoRotate = false, view2DPosition = null) {
        this._updateTexture = true;
        return this.isHidden() ? this.show(view2D, autoRotate, view2DPosition) : this.hide()
    }

    isNeedUpdateTexture() {
        return this._updateTexture;
    }

    updateTeleportFOV(yaw) {
        const teleport = this._getActiveTeleport();

        if (!teleport) return;

        yaw = yaw * Math.PI / 180;

        const {areaID} = teleport;
        const {properties:{scene:{rotateDirection}}} = this._areas.find((area) => area.id === areaID);
        const shift = Math.PI / 2;

        // yaw = normalizeYaw(yaw - rotateDirection - this._sceneAngle + shift);
        yaw = normalizeYaw(yaw - rotateDirection + shift);

        teleport.rotation.y = -yaw + shift * 3 / 2;

        this._render();
    }

    show(view2D = false, autoRotate = false, view2DPosition = null) {
        const changed = this._view2D !== view2D;
        this._view2D = view2D;

        if (view2DPosition) {
            this._setInitCameraPosition('2d', view2DPosition);
        }

        if(this._orbitControls && !this._orbitControls.enabled) {
            this._moveOut();
        }
        else if (this._inited && (changed || this._view2D)) {
            const offset = window.innerWidth > window.innerHeight ? 1 : 2;
            const {x, y, z} = this._view2D ? this._get2dCameraPosition().position : this._getStartCameraCoords(this._camera, this._planData.boundingBox, offset).vector;

            this._camera.position.set(x, y, z);
            this._orbitControls.update();
        }

        if (this._inited && autoRotate) this._startAutoRotate();


        return this._getCurrentSceneConfig()
            .then(({sceneID, yaw}) => {
                this._highlightSceneTeleport(sceneID);
                this.updateTeleportFOV(yaw);

                if (this._inited && this._floorSelect && view2D) {
                    const floorID = this._getLayerIdBySceneId(sceneID);

                    this._floorSelect.set(floorID);
                } else if (this._inited && this._floorSelect) {
                    // select all floors
                    this._floorSelect.set('0');
                }
            })
            .then(() => {
                this._wrapper.classList.add('show-3d');
            })
    }

    highlightSceneTeleport(sceneID) {
        const teleportBounding = this._getTeleportBoundingBySceneID(sceneID);

        if (teleportBounding) this._lastTeleportBounding = teleportBounding;

        this._highlightSceneTeleport(sceneID);
        this._render();
    }

    hide(triggerEvent = false, e) {
        if (e) e.stopPropagation();

        this._wrapper.classList.remove('show-3d');

        if (this._wheelInterval) this._stopAutoRotate();
        if (this._orientationHelper) this._orientationHelperHide();
        if (!this.isInited()) this.interruptIniting();

        if (triggerEvent) this._fireEvent('model.hidden');

        if(this._error) {
            this._error.remove();
        }
    }

    isHidden() {
        return !this._wrapper.classList.contains('show-3d');
    }

    isInited() {
        return this._inited;
    }

    resetConfig(planConfig, reinit = true) {
        // in case of async plan config changes
        if (reinit) {
            this._inited = false;
            this._generated = 0;
            this.interruptIniting();
        }

        this._planConfig = planConfig;
    }

    init(createTextures = true, skipRendering = false) {
        const {previewMode, t_3d_model_dimensions, measurementMode} = this._options;
        this._clearDataFromTransparent();
        this._renderer = new Three.WebGLRenderer({ preserveDrawingBuffer: true, alpha: true, antialias: false });
        this._generated = 0;
        this._updateTexture = true;

        if (!previewMode) this._triggerLoading();

        let scene3D = new Three.Scene();

        //RENDERER
        this.setBackground(styles.colors.grey);
        this._renderer.setSize(this._width, this._height);
        this._renderer.setPixelRatio(window.devicePixelRatio);

        let {width, height, unit, selectedLayer, layers} = this._planConfig;
        let scene = {width, height, unit, selectedLayer, layers};

        let viewModel3D = new ViewModel3D(scene, {width: this._width, height: this._height, previewMode, measurementMode});

        let {planData, promise} = viewModel3D.createSceneView(createTextures);

        scene3D.add(planData.plan);

        window.scene3D = scene3D;
        window.planData = planData;

        let aspectRatio = this._width / this._height;
        let camera = new Three.PerspectiveCamera(45, aspectRatio, 1, 300000);

        scene3D.add(camera);

        // Set position for the camera
        let cameraPositionX = -(planData.boundingBox.max.x - planData.boundingBox.min.x) / 2;
        let cameraPositionY = (planData.boundingBox.max.y - planData.boundingBox.min.y) / 2 * 10;
        let cameraPositionZ = (planData.boundingBox.max.z - planData.boundingBox.min.z) / 2;

        camera.position.set(cameraPositionX, cameraPositionY, cameraPositionZ);
        camera.up = new Three.Vector3(0, 1, 0);

        this._mouseDownEventBound = this._mouseDownEvent.bind(this);
        this._onTouchStartBound = this._onTouchStart.bind(this);
        this._onTouchEndBound = this._onTouchEnd.bind(this);
        this._mouseUpEventBound = this._mouseUpEvent.bind(this);
        this._touchMoveBound = this._touchMove.bind(this);

        if (!previewMode) {
            this._renderer.domElement.addEventListener('mousedown', this._mouseDownEventBound);
            this._renderer.domElement.addEventListener('touchstart', this._onTouchStartBound);
            this._renderer.domElement.addEventListener('touchend', this._onTouchEndBound);
            this._renderer.domElement.addEventListener('mouseup', this._mouseUpEventBound);
            this._renderer.domElement.addEventListener('touchmove', this._touchMoveBound);
        }

        this._renderer.domElement.style.display = 'block';

        // add the output of the renderer to the html element
        this._wrapper.appendChild(this._renderer.domElement);

        // create orbit controls
        let orbitController = new OrbitControls(camera, this._renderer.domElement);

        this._lines = this._getLines();
        this._areas = this._getAreas();
        this._vertices = this._getVertices();

        this._orbitControls = orbitController;
        this._orbitControls.screenSpacePanning = true;
        this._orbitControls.enabled = !previewMode;
        this._camera = camera;
        this._scene3D = scene3D;
        this._planData = planData;
        this._viewModel3D = viewModel3D;

        this.addLightToScene();

        this._updateVisibilityForTransparentDebounce = throttle(()=> {
            this._updateVisibilityForTransparent();
        }, 100);
        this._updateVisibilityBound = this._updateVisibility.bind(this);
        this._renderBound = this._render.bind(this);

        this._orbitControls.addEventListener('change', this._updateVisibilityBound);

        if (!previewMode && !measurementMode) {
            this._floorSelect = this._initFloorSelect();
            if(t_3d_model_dimensions) this._metricSelect = this._initMetricSelect();
        }

        if(!measurementMode){
            this._closeButton = this._initCloseButton();
        }

        camera.position.set(-600, 1100, 600);
        this._tween = new TWEEN.Tween(this._camera.position);

        Promise.all([isMetrical(), promise]).then(([metric, result]) => {
                if (!result || !result.boundingBox) return {config: null, data: null};

                this._metric = metric;

                if (!previewMode && !measurementMode) {
                    const value = Number(metric).toString();

                    this._updateMetricValues(metric);
                    if(t_3d_model_dimensions) this._metricSelect.set(value);
                }

                // in case of just texture generation
                if (skipRendering) return {data: result.data};

                let {data, boundingBox} = result;

                this._updateBoundingBox = boundingBox;

                return this._getCurrentSceneConfig().then((config) => ({config, data}))
            })
            .then(({config, data}) => {
                // in case of just texture generation
                if (skipRendering) return {data};

                const {sceneID} = config;

                this._addPoseRollPitch(sceneID);
                this._applyRotateAngle(this._planConfig, this._planData.plan);

                ({
                    plan: this._planData.plan,
                    boundingBox: this._planData.boundingBox
                } = ViewModel3D.updateBoundingBox(this._planData));

                this._orbitControls.update();

                if (!previewMode) {
                    this._triggerLoading();
                }

                this._highlightSceneTeleport(sceneID);
                if (measurementMode) {
                    this._addMeasurementTools();
                }

                this._render();
                this._inited = true;
                if (this._view2D) {
                    if (this._floorSelect) {
                        const floorID = this._getLayerIdBySceneId(sceneID);

                        this._floorSelect.set(floorID);
                    }

                    const {position: {x, y, z}} = this._get2dCameraPosition();

                    this._camera.position.set(x, y, z);
                    this._orbitControls.update();
                } else {
                    this._fitToObject(true);
                }

                if (!this._view2D && !measurementMode) this._startAutoRotate();

                if (!createTextures && !previewMode && !measurementMode) {
                    this._initOrientationHelper();
                }

                this._onWindowResize(previewMode);
            });

        return createTextures ? this._generateSprite(promise) : promise;
    }

    _addMeasurementTools(){
        this.measurementTool = new MeasurementTool(this._planData, {
            wrapper: this._wrapper, 
            renderer: this._renderer,
            orbitControls: this._orbitControls,
            scene3D: this._scene3D,
            camera: this._camera,
            
        });
       
        this.measurementTool.setSize(this._width, this._height);

        const labelRenderer = this.measurementTool.addLabelRenderer(this._width, this._height);
        
        this.measurementTool.on('render', (e) => {
            this._render();
        })

        this.measurementTool.on('on.mouse.down', (event) => {
            this._fireEvent('on.mouse.down', event);
         })

        this.measurementTool.on('on.mouse.move', (event) => {
            this._fireEvent('on.mouse.move', event);
        })

        this.measurementTool.on('close.polyline', (event) => {
            this._fireEvent('close.polyline', event);
        })

        this.measurementTool.on('on.add.point', (obj) => {
           this._fireEvent('measurementTool.add.point', obj);
        })

        this._wrapper.appendChild(labelRenderer.domElement);

        this.on('on.window.resize.end', (e) => {
            this.measurementTool.setSize(this._width, this._height);
            labelRenderer.setSize(this._width, this._height);
        })

        this.on('on.render', (e) => {
            labelRenderer.render(this._scene3D, this._camera);
        })
    }

    _addPoseRollPitch(sceneID) {
        const area = this._areas.find((area) => area.properties.scene.id === sceneID);
        if(!area) return ;

        const {posePitch, poseRoll} = area.properties.scene;
        const plan = this._scene3D.children.find(({name}) => name === 'plan');
        
        plan.rotation.x -= poseRoll;
        plan.rotation.z -= posePitch;
    }

    updateObjs(){
        this._lines = this._getLines();
        this._areas = this._getAreas();
        this._vertices = this._getVertices();
    }

    _generateSprite(promise) {
        return new Promise((resolve, rej) => {
            Promise.all([promise]).then((data) => {
                const planData = cloneDeep(data[0].data);
                const sprites = cloneDeep(data[0].data);

                Spritesmith.run(sprites, {algorithm: 'binary-tree', exportOpts: {format: 'jpeg'}, scale: RESOLUTION_SCALE_LOW}, function handleResult (err, result) {
                    resolve({plan: planData, miniPlan: result});
                });
            })
        })
    }

    rotate(delta) {
        if (this._wheelInterval) this._stopAutoRotate();

        this._orbitControls.rotateLeft(delta);
        this._orbitControls.update();
    }

    _addOutline() {
        addCopyShader(Three);
        addFXAAShader(Three);
        addEffectComposer(Three);
        addRenderPass(Three);
        addShaderPass(Three);
        addOutlinePass(Three);

        this._composer = new Three.EffectComposer(this._renderer);
        this._renderPass = new Three.RenderPass(this._scene3D, this._camera);
        this._composer.addPass(this._renderPass);
        this._outlinePass = new Three.OutlinePass(new Three.Vector2( this._width, this._height ), this._scene3D, this._camera);
        this._composer.addPass(this._outlinePass);
        this._effectFXAA = new Three.ShaderPass(Three.FXAAShader);
        this._effectFXAA.uniforms[ 'resolution' ].value.set( 1 / this._width, 1 / this._height );
        this._effectFXAA.renderToScreen = true;
        this._composer.addPass(this._effectFXAA);

        // ... add outline
        this._outlinePass.selectedObjects = this._planData.plan.children.map((obj) => {
            if (['area', 'wall'].includes(obj.name)) {
                return obj.children[0];
            }
        }).filter(Boolean);
    }

    interruptIniting(destroy = false) {
        this._triggerLoading(false);

        if (this._renderer) {
            this._renderer.domElement.removeEventListener('mousedown', this._mouseDownEventBound);
            this._renderer.domElement.removeEventListener('touchstart', this._onTouchStartBound);
            this._renderer.domElement.removeEventListener('mouseup', this._mouseUpEventBound);
            this._renderer.domElement.removeEventListener('touchmove', this._touchMoveBound);

            this._wrapper.removeChild(this._renderer.domElement);
        }

        if (this._floorSelect) {
            this._floorSelect.off();
            this._floorSelect.destroy();
        }

        if (this._metricSelect) {
            this._metricSelect.off();
            this._metricSelect.destroy();
        }

        if (this._closeButton) {
            this._closeButton.destroy();
        }

        if (this._orbitControls) {
            this._orbitControls.removeEventListener('change', this._updateVisibilityBound);
        }

        if (this._scene3D) {
            disposeScene(this._scene3D);

            if (this._planData && this._planData.plan) {
                this._scene3D.remove(this._planData.plan);
            }
        }

        if (this._viewModel3D) {
            if (destroy) {
                this._viewModel3D.destroy();
            } else {
                this._viewModel3D.interruptTexturesProcessing();
            }
        }

        this._scene3D = null;
        this._planData = null;
        this._camera = null;
        this._orbitControls = null;
        this._viewModel3D = null;
        this._floorSelect = null;
        this._metricSelect = null;
        this._closeButton = null;

        if (this._renderer && this._renderer.renderLists) {
            this._renderer.renderLists.dispose();
        }

        this._wheel(false);
        this._renderer = null;
    }

    _fitToObject(init = false) {
        const {fitCanvasToModel} = this._options;
        let offset = window.innerHeight >= window.innerWidth && !fitCanvasToModel ? 2 : 1;

        if (fitCanvasToModel) {
            let {width, height, aspect, fov, position: {x, y, z}} = this._getPreviewCameraSet(this._planData.boundingBox, this._camera.position);

            this._renderer.setSize(width, height);
            this._width = width;
            this._height = height;
            this._options.aspect = aspect;
            this._camera.position.set(x, y, z);
            this._camera.fov = fov;
            this._camera.aspect = aspect;
            this._camera.updateProjectionMatrix();
        } else {
            let {vector:{x, y, z}, maxDistance} = this._getStartCameraCoords(this._camera, this._planData.boundingBox, offset);

            this._camera.position.set(x, y, z);
            this._camera.far = maxDistance * 3;
            this._camera.updateProjectionMatrix();

            this._orbitControls.maxDistance = maxDistance * 2;
            this._orbitControls.saveState();
        }

        if (init) {
            let {fov, position: {x, y, z}} = this._camera;

            this._initPosition['3d'] = {
                camera: new Three.Vector3(x, y, z),
                fov,
                width: this._width,
                height: this._height
            }
        }
    }

    _getLayerIdBySceneId(sceneId) {
        let layerID = '';

        sceneId = sceneId.toString();

        let area = this._areas.find((area) => area.properties.scene.id === sceneId);

        if (area && !area.vertices.length) {
            Object.keys(this._planConfig.layers).some((id) => {
                const layer = this._planConfig.layers[id];

                if (!Object.keys(layer.vertices).length) return false;

                layerID = id;

                return true;
            });

        } else {
            Object.keys(this._planConfig.layers).some((id) => {
                let {areas} = this._planConfig.layers[id];

                return Object.keys(areas).some((area) => {
                    if (areas[area].properties.scene.id === sceneId.toString()) {
                        layerID = id;

                        return true;
                    }
                })
            });
        }

        return layerID;
    }

    _get2dCameraPosition() {
        if (this._initPosition['2d']) {
            const {camera: {x, y, z}, fov} = this._initPosition['2d'];

            return this._calculateCameraHeight({x,y,z}, fov);
        }

        let {fov, position:{x, y, z}} = this._camera;
        let position = new Three.Vector3(x, y, z), maxKey, minKey;

        ({position, maxKey, minKey} = this._calculateCameraHeight(position, fov));

        position[maxKey] = topDownCoef;
        position[minKey] *= topDownCoef;

        this._initPosition['2d'] = {camera: position, fov};

        return {position, maxKey, minKey};
    }

    isView2D() {
        return this._view2D;
    }

    getView2DPosition() {
        return this._initPosition['2d']
    }

    async isReady(maxAttempts = 10) {
        const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

        const check = () => {
            const {x, y, z} = this._planData.boundingBox.getSize();
            return x !== 0 && y !== 0 && z !== 0;
        };

        const attemptCheck = async (attempts) => {
            if (check()) {
                return true;
            } else if (attempts < maxAttempts) {
                await wait(300); 
                return attemptCheck(attempts + 1);
            }
            return false;
        };

        return attemptCheck(1);
    }

    moveTo2DView(sceneID) {
        if (this._view2D) return;

        const floorID = this._getLayerIdBySceneId(sceneID);

        if (floorID) this._toggleFloorVisability({floorID});

        const {position, maxKey, minKey} = this._get2dCameraPosition();
        const {target} = this._orbitControls;
        const size = this._planData.boundingBox.getSize();
        const aspect = size[maxKey] / size[minKey];
        const height = this._width / aspect;
        const deltaHeight = height - this._height;
        const startHeight = this._height;

        this._options.aspect = aspect;

        this._view2D = true;
        position[minKey] = this._camera.position[minKey];

        this._stopAutoRotate();

        return this._animateCamera(position, target, 500, () => {
                let state = (this._animationTimePointer - Date.now()) / 500;

                state = state < 0 ? 0 : state;

                this._height = startHeight + (1 - state) * deltaHeight;
                const aspect = this._width / this._height;

                this._renderer.setSize(this._width, this._height);
                this._camera.aspect = aspect;
                this._camera.updateProjectionMatrix();
            })
            .then(() => {
                const {position:{y}} = this._get2dCameraPosition();

                position[minKey] *= topDownCoef;
                position.y = y * 1.05;

                this._initPosition['2d'] = {camera: position, fov: this._camera.fov};

                return this._animateCamera(position, target, 500);
            })
    }

    destroy() {
        super.destroy();

        window.removeEventListener('resize', this._onWindowResizeBound, false);
        window.removeEventListener('orientationchange', this._onWindowResizeBound, false);
        window.removeEventListener('message', this._listener, false);

        if (this._orientationHelper) {
            this._destroyOrientationHelper();
        }

        this.interruptIniting(true);
    }
}

export default E3Plan3D;

